import React, { useState, useEffect, useMemo, Suspense } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  CircularProgress,
  Alert,
  Button,
  Typography
} from '@mui/material';
import { getContentTypeConfig } from './ContentTypeRegistry';
import { useResourceState, useResourceDispatch } from '../../context/ResourceContext';

// Accept ...props to pass down to the specific viewer
const ResourceViewerComponent = ({ content, ...props }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const { getResourceById } = useResourceState();
  const { upsertResource } = useResourceDispatch();

  // Try to find the resource in context, otherwise use the passed content
  const resourceFromContext = getResourceById(content.id);
  const currentResource = resourceFromContext || content;
  
  const resourceType = currentResource?.resource_type || currentResource?.type || 'unknown';

  useEffect(() => {
    // Only hydrate if it's marked as partial
    if (currentResource && currentResource._isPartial) {
      const hydrate = async () => {
        setIsLoading(true);
        setFetchError(null);
        try {
          const config = getContentTypeConfig(resourceType);
          if (config && config.fetcher) {
            console.log(`[ResourceViewer] Auto-hydrating ${resourceType} ${currentResource.id}`);
            const fullData = await config.fetcher(currentResource.id);
            if (fullData) {
              const merged = {
                ...currentResource,
                ...fullData,
                _isPartial: false
              };
              upsertResource(merged);
            }
          } else {
             console.warn(`[ResourceViewer] No fetcher defined for ${resourceType}, cannot hydrate.`);
          }
        } catch (err) {
          console.error("Hydration failed:", err);
          setFetchError(err.message);
        } finally {
          setIsLoading(false);
        }
      };
      hydrate();
    }
  }, [currentResource, resourceType, upsertResource]);

  const SpecificViewer = useMemo(() => {
    const config = getContentTypeConfig(resourceType);
    const viewer = config.viewer;
    if (!viewer) {
      return getContentTypeConfig('default').viewer;
    }
    return viewer;
  }, [resourceType]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6, flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }} color="text.secondary">Loading content...</Typography>
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => window.location.reload()}>Retry</Button>}>
          {fetchError}
        </Alert>
      </Box>
    );
  }

  if (!currentResource) {
    return <Box sx={{ p: 3 }}>Resource not found.</Box>;
  }

  return (
    <Suspense fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, flexDirection: 'column', alignItems: 'center' }}>
        <CircularProgress size={40} />
        <Typography sx={{ mt: 2 }}>Loading viewer...</Typography>
      </Box>
    }>
      {/* Pass ...props down to the specific viewer */}
      <SpecificViewer resource={currentResource} {...props} />
    </Suspense>
  );
};

ResourceViewerComponent.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.string,
    resource_type: PropTypes.string,
    _isPartial: PropTypes.bool,
  })
};

export default ResourceViewerComponent;